import React, { useState, useEffect } from 'react';
import { useTheme } from '../../context/ThemeContext';
import HeaderSection from '../../components/HeaderSection/HeaderSection';
import BottomSection from '../../components/BottomSection/BottomSection';
import { FaFont, FaCopy, FaRedo } from "react-icons/fa";

export default function LetterCounter() {
  const [inputText, setInputText] = useState("");
  const [selectedPlatform, setSelectedPlatform] = useState("Molec Tiles");
  const [characterCount, setCharacterCount] = useState(0);
  const [remainingChars, setRemainingChars] = useState(0);
     useTheme();

  const platformLimits = [
    { name: "Molec Tiles", limit: 52 },
    { name: "Mesh Discription", limit: 160 },
    { name: "Google Mail Post Content", limit: 202 },
    { name: "Instagram Caption/Comments", limit: 2000 },
    { name: "Twitter Text", limit: 282 },
    { name: "Music Literature", limit: 25 },
    { name: "Facebook WebText (Facebook)", limit: 477 },
    { name: "Facebook War Post (Ai)", limit: 62000 },
    { name: "Facebook Comment", limit: 6000 },
    { name: "Facebook Page Disruption", limit: 250 },
    { name: "Facebook Comments", limit: 50 },
    { name: "Facebook Message Messages", limit: 20000 },
    { name: "YouTube Videos text", limit: 30 },
    { name: "YouTube Video Description", limit: 5000 },
    { name: "Shepherd Caption", limit: 290 },
    { name: "Pinterest in money text", limit: 502 },
  ];

  useEffect(() => {
    const count = inputText.length;
    setCharacterCount(count);
    
    const currentPlatform = platformLimits.find(p => p.name === selectedPlatform);
    if (currentPlatform) {
      setRemainingChars(Math.max(0, currentPlatform.limit - count));
    }
  }, [inputText, selectedPlatform]);

  const copyToClipboard = () => {
    if (!inputText) return;
    navigator.clipboard.writeText(inputText);
  };

  const resetCounter = () => {
    setInputText("");
    setCharacterCount(0);
    const currentPlatform = platformLimits.find(p => p.name === selectedPlatform);
    if (currentPlatform) {
      setRemainingChars(currentPlatform.limit);
    }
  };

  return (
    <>
      <HeaderSection toolId="letterCounter" />
      <div className="max-w-4xl mx-auto p-4 lg:max-w-full lg:px-4 dark:text-white">
        <div className="bg-white dark:text-white rounded-xl dark:bg-gray-800 shadow-sm dark:shadow-gray-900/10 border border-gray-200 dark:border-gray-700 overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-green-500 to-green-600 p-6 text-white">
            <h1 className="text-2xl font-bold mb-2 flex items-center">
              <FaFont className="mr-3" />
              Letter Counter
            </h1>
            <p className="opacity-90">
              Count characters and check limits for different platforms
            </p>
          </div>

          {/* Main Content */}
          <div className="p-6">
            {/* Platform Selector */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Select Platform
              </label>
              <select 
                className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 focus:ring-green-500 focus:border-green-500"
                value={selectedPlatform}
                onChange={(e) => setSelectedPlatform(e.target.value)}
              >
                {platformLimits.map((platform, index) => (
                  <option key={index} value={platform.name}>
                    {platform.name} (Max: {platform.limit})
                  </option>
                ))}
              </select>
            </div>

            {/* Input Text Area */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Enter your text...
              </label>
              <textarea
                className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 focus:ring-green-500 focus:border-green-500 h-32"
                value={inputText}
                onChange={(e) => setInputText(e.target.value)}
                placeholder="Type or paste your text here..."
              />
            </div>

            {/* Character Count Display */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-6">
              <div className="bg-gray-100 dark:bg-gray-700 p-4 rounded-md">
                <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Character Count</h3>
                <p className="text-2xl font-bold text-green-600 dark:text-green-400">
                  {characterCount}
                </p>
              </div>
              <div className="bg-gray-100 dark:bg-gray-700 p-4 rounded-md">
                <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Remaining Characters</h3>
                <p className={`text-2xl font-bold ${remainingChars < 0 ? 'text-red-600 dark:text-red-400' : 'text-green-600 dark:text-green-400'}`}>
                  {remainingChars}
                </p>
              </div>
            </div>

            {/* Platform Limit Info */}
            <div className="mb-6 bg-gray-100 dark:bg-gray-700 p-4 rounded-md">
              <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Current Platform Limit</h3>
              <p className="text-lg">
                {platformLimits.find(p => p.name === selectedPlatform)?.name}: {platformLimits.find(p => p.name === selectedPlatform)?.limit} characters
              </p>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-wrap gap-3">
              <button
                onClick={resetCounter}
                className="px-4 py-2 bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 rounded-md flex items-center gap-2 transition"
              >
                <FaRedo /> Reset
              </button>
              <button
                onClick={copyToClipboard}
                disabled={!inputText}
                className={`px-4 py-2 rounded-md flex items-center gap-2 transition ${inputText ? 'bg-green-500 hover:bg-green-600 text-white' : 'bg-gray-200 dark:bg-gray-700 cursor-not-allowed'}`}
              >
                <FaCopy /> Copy Text
              </button>
            </div>
          </div>

          {/* Footer */}
          <div className="bg-gray-100 dark:text-white dark:bg-gray-800 p-4 text-center text-sm text-gray-600">
            <p>Track character counts and platform limits for your content</p>
          </div>
        </div>
      </div>
      <BottomSection toolId="letterCounter" />
    </>
  );
}